import { useLayoutEffect, useRef, useState } from 'react';

export const SVG_ANIMATION_CONFIG = {
  PERCENTAGE_TO_DECIMAL: 100,
  CLOSED_LOOP_DIVISOR: 2,
  MIN_VISIBLE_PERCENTAGE: 1, // Minimum 1% fill for visibility
} as const;

export interface PathAnimationParams {
  pathLength: number;
  fillPercentage: number;
  minVisiblePercentage?: number;
}

/**
 * Calculates SVG strokeDashoffset for a closed loop path animation.
 * Since the path is a loop (going there and back), the total length is double the visual arc.
 * We divide by 2 to ensure 100% fill corresponds to traversing only the first half of the loop.
 *
 * @param params - Animation parameters
 * @param params.pathLength - Total length of SVG path (from getTotalLength())
 * @param params.fillPercentage - Current fill percentage (0-100)
 * @param params.minVisiblePercentage - Minimum visible percentage (default: 1)
 * @returns Calculated strokeDashoffset value
 */
export const calculatePathOffset = ({
  pathLength,
  fillPercentage,
  minVisiblePercentage = SVG_ANIMATION_CONFIG.MIN_VISIBLE_PERCENTAGE,
}: PathAnimationParams): number => {
  const visiblePercentage = fillPercentage > 0 ? Math.max(minVisiblePercentage, fillPercentage) : 0;
  return (
    pathLength -
    (pathLength * visiblePercentage) /
      SVG_ANIMATION_CONFIG.CLOSED_LOOP_DIVISOR /
      SVG_ANIMATION_CONFIG.PERCENTAGE_TO_DECIMAL
  );
};

export const SVG_VIEWBOX_WIDTH = 124;
export const SVG_VIEWBOX_HEIGHT = 115;

export const useSVGPathLength = (pathCount: number = 1) => {
  const pathRefs = useRef<(SVGPathElement | null)[]>(Array(pathCount).fill(null));
  const [pathLengths, setPathLengths] = useState<number[]>([]);

  useLayoutEffect(() => {
    const lengths = pathRefs.current.map((pathEl) => {
      if (pathEl) {
        return pathEl.getTotalLength();
      }
      return 0;
    });

    setPathLengths(lengths);
  }, [pathCount]);

  return { pathRefs, pathLengths };
};
